if(!customElements.get('get-your-perfect-fit')) {
    class GetYourPerfectFit extends HTMLElement {
        constructor() {
            super();
            // In-memory cache for per-handle parsed fragments to avoid refetching + reparsing.
            this.productCache = new Map();
            // Track latest request to prevent stale responses from applying.
            this.requestToken = 0;
            this.activeAbortController = null;
            this.fadeTimeoutId = null;
        }
        connectedCallback() {
            this.selectors = {
                products: this.querySelectorAll('.collection-products .product'),
                featuredImage: this.querySelector('.selected-product .image'),
                defaultTitle: this.querySelector('.selected-product .product-details .default-title'),
                productTitle: this.querySelector('.selected-product .product-details .product-title'),
                productPrice: this.querySelector('.selected-product .product-details .product-price'),
                selectedImages: this.querySelectorAll('.product-selected-images .image'),
                productBadge: this.querySelector('.product-badge'),
                continue: this.querySelector('.continue-cta button'), 
                goToProduct: this.querySelector('.continue-cta .go-to-product'),
                buyDialog: document.querySelector('fit-buy-dialog'),
            }
            this.setDefaultTitle(this.selectors.defaultTitle ? this.selectors.defaultTitle.textContent : '');
            const activeProduct = this.querySelector('.collection-products .product.active');
            if(activeProduct && activeProduct.dataset.productHandle) {
                this.updateGoToProduct(activeProduct.dataset.productHandle);
            }
            if(this.selectors.products.length > 0) {
                this.selectors.products.forEach(product => {
                    product.addEventListener('click', event => {
                        this.selectors.products.forEach(item => {
                            item.classList.remove('active');
                        })
                        if(event.target.closest('.product')) {
                            const handle = event.target.closest('.product').dataset.productHandle;
                            this.productQuery(handle);
                            this.updateGoToProduct(handle);
                            event.target.closest('.product').classList.add('active');
                        }
                    })
                })
            }
            if(this.selectors.continue) {
                this.selectors.continue.addEventListener('click', () => {
                    const event = new CustomEvent("fitBuyDialog", {
                        detail: { productHandle: this.dataset.productHandle, mode: 'none' }
                    });
                    document.dispatchEvent(event);
                })
            }
            this.setupPreloadObserver();
        }
        setupPreloadObserver() {
            if(GetYourPerfectFit.preloadStarted) {
                return;
            }
            if(!('IntersectionObserver' in window)) {
                return;
            }
            const observer = new IntersectionObserver((entries, obs) => {
                entries.forEach(entry => {
                    if(entry.isIntersecting) {
                        GetYourPerfectFit.preloadStarted = true;
                        obs.unobserve(entry.target);
                        const handles = Array.from(this.selectors.products || [])
                            .map(product => product.dataset.productHandle)
                            .filter(Boolean);
                        if(handles.length > 0) {
                            this.preloadProducts(handles);
                        }
                    }
                });
            }, { rootMargin: '600px 0px' });
            observer.observe(this);
        }
        buildCacheEntry(htmlDoc) {
            const image = htmlDoc.getElementById('fit-selected-image');
            const productTitle = htmlDoc.getElementById('product-title');
            const productSelectedImages = htmlDoc.querySelectorAll('.product-selected-images .image');
            const productPrice = htmlDoc.getElementById('product-price');
            const productBadge = htmlDoc.getElementById('product-badge');

            return {
                featuredImageHtml: this.sanitizeImageHtml(image ? image.innerHTML : ''),
                productTitleText: productTitle ? productTitle.innerText : '',
                selectedImagesHtml: productSelectedImages.length > 0
                    ? Array.from(productSelectedImages, (item) => this.sanitizeImageHtml(item.innerHTML))
                    : [],
                productPriceHtml: productPrice ? productPrice.innerHTML : '',
                productBadgeHtml: productBadge ? productBadge.innerHTML : '',
            };
        }
        sanitizeImageHtml(html) {
            if(!html) {
                return '';
            }
            const temp = document.createElement('div');
            temp.innerHTML = html;
            temp.querySelectorAll('img').forEach(img => {
                const existingStyle = img.getAttribute('style') || '';
                const hideStyle = 'color:transparent;font-size:0;line-height:0;text-indent:-9999px;';
                img.setAttribute('style', `${existingStyle}${hideStyle}`);
            });
            return temp.innerHTML;
        }
        buildDialogCacheEntry(htmlDoc) {
            const image = htmlDoc.getElementById('product-image');
            const productTitle = htmlDoc.getElementById('product-title');
            const productBadge = htmlDoc.getElementById('product-badge');
            const productDescription = htmlDoc.getElementById('product-description');
            const collectionProducts = htmlDoc.getElementById('collection-products');
            const productPrice = htmlDoc.getElementById('product-price');
            const adultVersion = htmlDoc.getElementById('adult-version');
            const youthVersion = htmlDoc.getElementById('youth-version');

            return {
                imageHtml: image ? image.innerHTML : '',
                productTitleText: productTitle ? productTitle.innerText : '',
                productBadgeHtml: productBadge ? productBadge.innerHTML : '',
                productDescriptionHtml: productDescription ? productDescription.innerHTML : '',
                collectionProductsHtml: collectionProducts ? collectionProducts.innerHTML : '',
                productPriceHtml: productPrice ? productPrice.innerHTML : '',
                adultVersionValue: adultVersion ? adultVersion.value : '',
                youthVersionValue: youthVersion ? youthVersion.value : '',
            };
        }
        async preloadProducts(handles) {
            const queue = handles.filter(handle => handle && !this.productCache.has(handle));
            const maxConcurrent = 3;
            let index = 0;

            const worker = async () => {
                while(index < queue.length) {
                    const handle = queue[index];
                    index += 1;
                    if(this.productCache.has(handle)) {
                        continue;
                    }
                    try {
                        const response = await fetch(`/products/${handle}?section_id=fit-product-cached`);
                        if(!response.ok) {
                            throw new Error(`Preload failed with status ${response.status}`);
                        }
                        const html = await response.text();
                        const parser = new DOMParser();
                        const htmlDoc = parser.parseFromString(html, 'text/html');
                        const cacheEntry = this.buildCacheEntry(htmlDoc);
                        // Cache only; do not update UI or active state.
                        this.productCache.set(handle, cacheEntry);

                        if(!window.fitBuyDialogCache) {
                            window.fitBuyDialogCache = new Map();
                        }
                        if(!window.fitBuyDialogCache.has(handle)) {
                            const dialogResponse = await fetch(`/products/${handle}?section_id=fit-buy-dialog-cached`);
                            if(!dialogResponse.ok) {
                                throw new Error(`Preload dialog failed with status ${dialogResponse.status}`);
                            }
                            const dialogHtml = await dialogResponse.text();
                            const dialogDoc = parser.parseFromString(dialogHtml, 'text/html');
                            const dialogCacheEntry = this.buildDialogCacheEntry(dialogDoc);
                            window.fitBuyDialogCache.set(handle, dialogCacheEntry);
                        }
                    } catch(error) {
                        console.error('Preload request failed:', error);
                    }
                }
            };

            const workers = Array.from({ length: Math.min(maxConcurrent, queue.length) }, () => worker());
            await Promise.all(workers);
        }
        getDisplayTitles(rawTitle) {
            const cleanedTitle = (rawTitle || '')
                .replace(/F\.I\.T\./gi, '')
                .replace(/\s+/g, ' ')
                .trim();

            if(/^vector\b/i.test(cleanedTitle)) {
                return {
                    defaultTitle: 'Vector with OFT',
                    productTitle: cleanedTitle.replace(/^vector\b/i, '').trim(),
                };
            }

            return {
                defaultTitle: '',
                productTitle: cleanedTitle,
            };
        }
        setDefaultTitle(text) {
            if(!this.selectors.defaultTitle) return;
            const defaultTitle = text || '';
            this.selectors.defaultTitle.textContent = '';
            const parts = defaultTitle.split(/(TM\b)/);

            parts.forEach(part => {
                if(part === 'TM') {
                    const trademark = document.createElement('sup');
                    trademark.className = 'trademark';
                    trademark.textContent = 'TM';
                    this.selectors.defaultTitle.appendChild(trademark);
                    return;
                }
                this.selectors.defaultTitle.appendChild(document.createTextNode(part));
            });
        }
        applyCachedFragments(cacheEntry) {
            // Apply cached fragments to DOM; no network/parsing work needed.
            if(this.selectors.featuredImage) {
                this.selectors.featuredImage.innerHTML = cacheEntry.featuredImageHtml || '';
            }
            const displayTitles = this.getDisplayTitles(cacheEntry.productTitleText);
            this.setDefaultTitle(displayTitles.defaultTitle);
            this.selectors.productTitle && (this.selectors.productTitle.innerText = displayTitles.productTitle);
            if(this.selectors.selectedImages.length > 0) {
                this.selectors.selectedImages.forEach((image, index) => {
                    image.innerHTML = cacheEntry.selectedImagesHtml[index] || '';
                })
            }
            if(this.selectors.productPrice) {
                this.selectors.productPrice.innerHTML = cacheEntry.productPriceHtml || '';
            }
            if(this.selectors.productBadge) {
                this.selectors.productBadge.innerHTML = cacheEntry.productBadgeHtml || '';
            }
        }
        async productQuery(handle) {
            this.dataset.productHandle = handle;
            this.updateGoToProduct(handle);
            if(this.productCache.has(handle)) {
                // Cached fragments make repeated clicks instant (no fetch + parse).
                this.applyCachedFragments(this.productCache.get(handle));
                return;
            }

            const currentToken = ++this.requestToken;
            if(this.activeAbortController) {
                this.activeAbortController.abort();
            }
            this.activeAbortController = new AbortController();
            this.selectors.continue && (this.selectors.continue.disabled = true);

            try {
                const response = await fetch(`/products/${handle}?section_id=fit-product-cached`, {
                    signal: this.activeAbortController.signal
                });
                if(!response.ok) {
                    throw new Error(`Request failed with status ${response.status}`);
                }
                const html = await response.text();

                // Ignore stale responses from older requests.
                if(currentToken !== this.requestToken) {
                    return;
                }

                const parser = new DOMParser();
                const htmlDoc = parser.parseFromString(html, 'text/html');
                const cacheEntry = this.buildCacheEntry(htmlDoc);

                // Cache parsed fragments so each handle is fetched/parsed only once per page load.
                this.productCache.set(handle, cacheEntry);
                this.applyCachedFragments(cacheEntry);
            } catch(error) {
                if(error.name !== 'AbortError') {
                    console.error('Fetch request failed:', error);
                }
            } finally {
                // Re-enable only if this is still the latest request.
                if(currentToken === this.requestToken) {
                    this.selectors.continue && (this.selectors.continue.disabled = false);
                }
            }
        }
        updateGoToProduct(handle) {
            if(this.selectors.goToProduct && handle) {
                this.selectors.goToProduct.href = `/products/${handle}`;
            }
        }
    }
    customElements.define('get-your-perfect-fit', GetYourPerfectFit);
}
